import {useEffect, useRef, useState} from "react";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {RiderSettlement, riderSettlementListFromDto} from "../types/riderSettlement.types";
import {riderSettlementApi} from "../api/riderSettlementApi";
import { formatDateToDisplay } from "@/shared/utils/formatter";
import { buildExcelDownloadUrl } from "@/shared/utils/settlementApiHelpers";
import {useSettlementFile} from "../../../shared/hooks/useSettlementFile";
import { supplierApi } from "@/features/partner/supplierStatus/api/supplierApi";
import { Supplier } from "@/features/partner/supplierStatus/types/supplier.types";
import { supplierListFromDto } from "@/features/partner/supplierStatus/utils/supplierHelpers";

/**
 * 라이더 주정산내역 데이터 관리 훅 (27개 컬럼)
 *
 * @param userSupplierId - 협력사 사용자 ID (협력사 로그인 시)
 * @param userRiderId - 라이더 사용자 ID (라이더 로그인 시)
 * @param skipInitialFetch - 초기 자동 조회 건너뛰기 여부
 */
export const useRiderSettlementData = (userSupplierId?: string | null, userRiderId?: string | null, skipInitialFetch = false) => {
    const { showToast } = useToast();
    const fileHelper = useSettlementFile('/api/v1/rider-settlement');
    const [settlements, setSettlements] = useState<RiderSettlement[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [uploading, setUploading] = useState<boolean>(false);
    const [latestStartDate, setLatestStartDate] = useState<string>('');
    const [latestEndDate, setLatestEndDate] = useState<string>('');
    const [initialSettlementsFetched, setInitialSettlementsFetched] = useState<boolean>(false);

    const hasFetchedSuppliers = useRef(false);
    const hasFetchedLatestDate = useRef(false);
    const hasFetchedInitialSettlements = useRef(false);

    // 라이더 사용자 여부
    const isRiderUser = !!userRiderId;

    // 협력사 목록 조회 (라이더 사용자는 조회 안 함)
    useEffect(() => {
        if (hasFetchedSuppliers.current || isRiderUser) {
            if (isRiderUser) {
                hasFetchedSuppliers.current = true; // 라이더는 협력사 조회 불필요
            }
            return;
        }

        let isCancelled = false;

        const fetchSuppliers = async () => {
            try {
                const response = await supplierApi.getSupplierList();
                if (!isCancelled) {
                    const supplierList = supplierListFromDto(response);
                    setSuppliers(supplierList.suppliers);
                    hasFetchedSuppliers.current = true;
                }
            } catch {
                if (!isCancelled) {
                    showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                    setSuppliers([]);
                    hasFetchedSuppliers.current = true;
                }
            }
        };

        fetchSuppliers();
        return () => {
            isCancelled = true;
        };
    }, [showToast, isRiderUser]);

    // 최근 정산일자 조회 (시작일/종료일)
    useEffect(() => {
        if (hasFetchedLatestDate.current) return;

        let isCancelled = false;

        const fetchLatestDate = async () => {
            try {
                const response = await riderSettlementApi.getLatestSettlementDate();
                if (!isCancelled) {
                    const formattedStartDate = formatDateToDisplay(response.latestStartDate);
                    const formattedEndDate = formatDateToDisplay(response.latestEndDate);
                    setLatestStartDate(formattedStartDate);
                    setLatestEndDate(formattedEndDate);
                    hasFetchedLatestDate.current = true;
                }
            } catch {
                if (!isCancelled) {
                    // 에러가 발생해도 빈 문자열로 처리 (데이터가 없을 수 있음)
                    setLatestStartDate('');
                    setLatestEndDate('');
                    hasFetchedLatestDate.current = true;
                }
            }
        };

        fetchLatestDate();
        return () => {
            isCancelled = true;
        };
    }, [showToast]);

    // 최근 정산일자로 정산내역 조회 (초기 로딩 시에만, skipInitialFetch가 false일 때만)
    useEffect(() => {
        // skipInitialFetch가 true이면 자동 조회 건너뛰기
        if (skipInitialFetch) {
            hasFetchedInitialSettlements.current = true;
            return;
        }

        // 이미 조회했으면 return
        if (hasFetchedInitialSettlements.current) return;

        // 아직 로딩 중이면 대기
        if (loading) return;

        // latestStartDate/latestEndDate가 없으면 데이터 없음으로 처리
        if (!latestStartDate || !latestEndDate) {
            hasFetchedInitialSettlements.current = true;
            setInitialSettlementsFetched(true);
            return;
        }

        // 라이더가 아닌 경우 suppliers가 없으면 return (본사 사용자는 예외)
        if (!isRiderUser && suppliers.length === 0 && userSupplierId !== null) return;

        let isCancelled = false;

        const fetchSettlements = async () => {
            try {
                let supplyId: string | undefined;
                let riderName: string | undefined;

                if (isRiderUser) {
                    // 라이더 사용자: 자신의 데이터만 조회
                    riderName = userRiderId || undefined;
                } else if (userSupplierId) {
                    // 협력사 사용자: 해당 협력사 데이터만 조회
                    supplyId = userSupplierId;
                } else {
                    // 본사 사용자: 전체 데이터 조회 (supplierId 없음)
                    supplyId = undefined;
                }

                const response = await riderSettlementApi.getRiderSettlementList(
                    latestStartDate,
                    latestEndDate,
                    riderName,
                    supplyId
                );

                if (!isCancelled) {
                    const settlementList = riderSettlementListFromDto(response);
                    setSettlements(settlementList);
                    hasFetchedInitialSettlements.current = true;
                    setInitialSettlementsFetched(true);
                }
            } catch (error) {
                if (!isCancelled) {
                    showToast('정산내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                    setSettlements([]);
                    hasFetchedInitialSettlements.current = true;
                    setInitialSettlementsFetched(true);
                }
            }
        };

        fetchSettlements();
        return () => {
            isCancelled = true;
        };
    }, [latestStartDate, latestEndDate, loading, showToast, suppliers, userSupplierId, userRiderId, isRiderUser, skipInitialFetch]);

    // 모든 데이터가 로드되었는지 확인
    useEffect(() => {
        if (hasFetchedSuppliers.current && hasFetchedLatestDate.current) {
            setLoading(false);
        }
    }, [suppliers, latestStartDate, latestEndDate]);

    /**
     * 최신 정산일자 재조회
     */
    const refreshLatestDate = async (): Promise<{ startDate: string; endDate: string } | null> => {
        try {
            const response = await riderSettlementApi.getLatestSettlementDate();
            const formattedStartDate = formatDateToDisplay(response.latestStartDate);
            const formattedEndDate = formatDateToDisplay(response.latestEndDate);
            setLatestStartDate(formattedStartDate);
            setLatestEndDate(formattedEndDate);
            return { startDate: formattedStartDate, endDate: formattedEndDate };
        } catch (error) {
            showToast('최근 정산일자 조회에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        }
    }

    /**
     * 정산내역 재조회
     * @returns 조회된 정산내역 건수 (에러 시 -1)
     */
    const refreshSettlements = async (
        acctStDate: string,
        acctEdDate: string,
        riderName?: string,
        supplierId?: string
    ): Promise<number> => {
        try {
            // 라이더 사용자인 경우 자신의 ID로 강제 필터링
            const finalRiderName = isRiderUser ? (userRiderId || riderName) : riderName;

            const response = await riderSettlementApi.getRiderSettlementList(
                acctStDate,
                acctEdDate,
                finalRiderName,
                supplierId
            );

            const settlementList = riderSettlementListFromDto(response);

            setSettlements(settlementList);
            return settlementList.length;
        } catch (error) {
            showToast('정산내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setSettlements([]);
            return -1;
        }
    };

    /**
     * 엑셀 업로드
     */
    const uploadExcel = async (file: File, userId: string): Promise<boolean> => {
        setUploading(true);
        try {
            const result = await fileHelper.uploadExcel(file, userId);
            return result;
        } catch (error) {
            showToast('엑셀 파일 업로드 중 오류가 발생했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setUploading(false);
        }
    };

    /**
     * 엑셀 다운로드
     */
    const downloadExcel = async (
        acctStDate: string,
        acctEdDate: string,
        riderName?: string,
        supplierId?: string
    ): Promise<void> => {
        try {
            setLoading(true);
            // 라이더 사용자인 경우 자신의 ID로 강제 필터링
            const finalRiderName = isRiderUser ? (userRiderId || riderName) : riderName;

            const url = buildExcelDownloadUrl('/api/v1/rider-settlement', {
                acctStDate,
                acctEdDate,
                riderName: finalRiderName && finalRiderName.trim() !== '' ? finalRiderName : undefined,
                supplierId
            });

            const fileName = fileHelper.createDateRangeFileName(
                acctStDate,
                acctEdDate,
                '라이더주정산내역'
            );
            await fileHelper.downloadExcel(url, () => fileName);
            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    return {
        settlements,
        suppliers,
        latestStartDate,
        latestEndDate,
        loading,
        uploading,
        refreshLatestDate,
        initialSettlementsFetched,
        refreshSettlements,
        uploadExcel,
        downloadExcel
    };
};
